/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo } from 'react';

import { cleanString } from '../helpers/cleanString';
import { FreshchatPlatform } from '../model/enum/FreshchatPlatform.enum';
import { useUpdateUserFreshChatRestoreId } from '../services/myProfile/useUpdateUserFreshChatRestoreId';

declare global {
  interface Window {
    fcWidget: any;
    fcWidgetMessengerConfig: any;
  }
}

type FreshChatConfig = {
  mode: 'auto' | 'manual';
  autoShowDelay?: number;
};

export const useFreshChat = (config?: FreshChatConfig) => {
  const mode = config?.mode ?? 'auto';
  const autoShowDelay = config?.autoShowDelay ?? 5000;

  const rawUserData = localStorage.getItem('userData');
  const userData = rawUserData ? JSON.parse(rawUserData) : null;
  const hasSpentBefore = useMemo(
    () => userData?.has_spent_before ?? false,
    [userData?.has_spent_before]
  );
  const freshchatPlatform = useMemo(
    () => userData?.freshdesk_platform ?? FreshchatPlatform.WIDGET,
    [userData?.freshdesk_platform]
  );
  const { mutate: saveRestoreId } = useUpdateUserFreshChatRestoreId();
  const restoreId = userData?.freshchat_restore_id ?? null;

  const _innerShowWidget = (open: boolean = false) => {
    if (window.fcWidget) {
      window.fcWidget.setConfig({ config: { headerProperty: { hideChatButton: false } } });
      window.fcWidget.show();
      if (open) {
        setTimeout(() => {
          window.fcWidget.open();
        }, 50);
      }
    }
  };

  const _innerHideWidget = () => {
    if (window.fcWidget) {
      window.fcWidget.setConfig({ config: { headerProperty: { hideChatButton: true } } });
      window.fcWidget.close();
      window.fcWidget.hide();
    }
  };

  useEffect(() => {
    const platform = cleanString(freshchatPlatform);
    const isContactPage = window.location.pathname === '/members-area/contact-us';
    const isVerificationPage = window.location.pathname === '/email-verification-instruction';
    const isAllowedPage = isContactPage || isVerificationPage;

    let timeoutId: NodeJS.Timeout | null = null;
    let checkInterval: NodeJS.Timeout | null = null;

    const shouldShowWidgetLauncher =
      isContactPage && hasSpentBefore && platform === FreshchatPlatform.CHAT;

    const performInitialization = () => {
      if (window.fcWidget) {
        if (!isAllowedPage) {
          _innerHideWidget();
        }
        if (shouldShowWidgetLauncher) {
          _innerShowWidget();
        }
      }

      if (
        window.fcWidget &&
        (isVerificationPage ||
          (isContactPage && hasSpentBefore && platform === FreshchatPlatform.CHAT))
      ) {
        window.fcWidget.init({
          token: '98a0a2f9-e360-495e-9f36-c88b0addb4ee',
          host: 'https://cdncloud-help.freshchat.com',
          widgetUuid: 'd4767699-f7df-417d-8031-22f6e2cad441',
          externalId: userData?.id ?? 'Unknown ID',
          restoreId,
          firstName: userData?.profile?.username ?? 'Unknown name',
          email: userData?.email ?? 'Unknown email',
          config: { headerProperty: { hideChatButton: !shouldShowWidgetLauncher } }
        });

        window.fcWidget.on('user:created', async (resp: any) => {
          const { status, data } = resp;
          if (status === 200 && data.restoreId && userData?.id) {
            saveRestoreId(data.restoreId);
          }
        });

        if (isContactPage && mode === 'auto') {
          timeoutId = setTimeout(() => {
            _innerShowWidget(true);
          }, autoShowDelay);
        }
      }
    };

    if (!window.fcWidget) {
      checkInterval = setInterval(() => {
        if (window.fcWidget) {
          clearInterval(checkInterval!);
          performInitialization();
        }
      }, 100);
    } else {
      performInitialization();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }

      _innerHideWidget();
      if (window.fcWidget) {
        window.fcWidget.off('user:created');
      }
    };
  }, [
    mode,
    autoShowDelay,
    userData?.id,
    hasSpentBefore,
    freshchatPlatform,
    restoreId,
    saveRestoreId,
    window.location.pathname
  ]);

  const showWidget = useCallback(() => {
    if (window.fcWidget) {
      _innerShowWidget(true);
    } else {
      const waitForWidget = setInterval(() => {
        if (window.fcWidget) {
          clearInterval(waitForWidget);
          _innerShowWidget(true);
        }
      }, 100);
      setTimeout(() => clearInterval(waitForWidget), 5000);
    }
  }, []);

  return { showWidget };
};
